import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AuthActions } from "src/redux/Auth/actions";
import { ModalActions } from "src/redux/Modal/actions";
import { getTermsState } from "src/utils/auth";
import Button from "src/components/common/Button";
import styles from "./styles.module.scss";

/**
 * Shown to a user who was already signed in when new terms were published.
 *
 * Its only control signs them out, because that is the way back in: the gateway
 * has already marked their session behind the gate, so the next request they
 * make is refused. Signing in again brings them to the consent modal.
 */
const TermsChanged = () => {
    const dispatch = useDispatch();
    return (
        <div className={styles.notice} data-testid="terms-changed-notice">
            <div className={styles.title}>The terms have been updated</div>
            <div className={styles.body}>
                Terms of Use and the Privacy Policy have been updated since you
                signed in. Sign in again to read them and accept before
                continuing.
            </div>
            <div className={styles.footer}>
                <Button
                    value="Sign in again"
                    onClick={() => dispatch(AuthActions.Logout())}
                    dataTestId="terms-changed-signout"
                />
            </div>
        </div>
    );
};

/**
 * Raises the notice when new terms are published under a signed-in user.
 *
 * Pushed, not polled: `platform:terms` is granted to every session by the
 * connect proxy, and publishing announces the new version on it, so an open tab
 * finds out at once rather than on its next request.
 *
 * The push is not what enforces this. Publishing marks every live session behind
 * the gate in the same transaction, so a client that never hears the
 * announcement is refused anyway; this is what makes it immediate and explains
 * itself rather than failing a request the user did not expect to fail.
 */
export default () => {
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state) => state.Auth.isLoggedIn);
    const termsRequired = useSelector((state) => state.Auth.termsRequired);
    const termsPublished = useSelector((state) => state.Auth.termsPublished);

    useEffect(() => {
        // Nothing to say before sign-in, nor while the consent modal is already
        // up: that user is being asked right now.
        if (!isLoggedIn || termsRequired || !termsPublished) return;

        // The version this session signed in with. An announcement of the same
        // version is the one already accepted, so it is not news.
        if (termsPublished === getTermsState().version) return;

        dispatch(
            ModalActions.Open(TermsChanged, {
                ignoreClickOutside: true,
                onClose: () => dispatch(AuthActions.Logout()),
            })
        );
    }, [dispatch, isLoggedIn, termsRequired, termsPublished]);

    return null;
};
